import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Alert, Autocomplete, Button, Link, OutlinedInput, Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Modal, { CommonModalProps } from "..";
import { DbOrderManualTaskPlanningResourceInfos } from "../../../Core/Api/Types/db-order-manual-task-planning-resource-infos";
import { GroupPlanningViewInfos } from "../../../Core/Api/Types/group-planning-view-infos";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import { routePaths } from "../../../Router/router";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { useTranslation } from "react-i18next";

export type ModalCreateOrUpdateResourceViewElementProps = {
  mode: "create" | "update";

  availableResources: DbOrderManualTaskPlanningResourceInfos[];
  resourceViewToUpdate?: GroupPlanningViewInfos;
  addResourceViewToGroup: (resourceView: GroupPlanningViewInfos) => void;
  editResourceViewOfGroup: (resourceView: GroupPlanningViewInfos) => void;
};

export type ModalCreateOrUpdateResourceViewProps = CommonModalProps<ModalCreateOrUpdateResourceViewElementProps>;

const ModalCreateOrUpdateResourceView = React.forwardRef<any, ModalCreateOrUpdateResourceViewProps>((props, ref) => {
  const {
    mode = "create",
    availableResources = [],
    resourceViewToUpdate,
    addResourceViewToGroup,
    editResourceViewOfGroup,
    closeModal,
  } = props;

  const navigate = useNavigate();
  const { t } = useTranslation();

  const formId = "modal-create-or-update-resource-view";
  const isNameFieldValid = useAppSelector(GetFormIsValid(formId));

  const [name, setName] = useState("");
  const [selectedResources, setSelectedResources] = useState<DbOrderManualTaskPlanningResourceInfos[]>([]);

  const handleNavigateToCreateResources = () => {
    closeModal();
    navigate(routePaths.settingsPlanningResources.path);
  };

  const handleAddView = () => {
    addResourceViewToGroup &&
      addResourceViewToGroup({
        date: moment().toDate(),
        name: name,
        resourcesIds: selectedResources.map((resource) => resource.id),
      });
    closeModal && closeModal();
  };

  const handleEditView = () => {
    editResourceViewOfGroup &&
      editResourceViewOfGroup({
        date: moment().toDate(),
        name: name,
        resourcesIds: selectedResources.map((resource) => resource.id),
      });
    closeModal && closeModal();
  };

  useEffect(() => {
    if (mode === "update" && resourceViewToUpdate && availableResources && availableResources.length) {
      setName(resourceViewToUpdate.name);
      setSelectedResources(availableResources.filter((r) => resourceViewToUpdate.resourcesIds.includes(r.id)));
    }
  }, []);

  return (
    <Modal
      title={mode === "create" ? t("modal:body.createNewResourceView") : t("modal:body.updateResourceView")}
      reference={ref}
      closeModal={closeModal}
      width={600}
      footer={
        <Stack spacing={1}>
          <Alert severity="warning">{t("alert:info.changeWillStoreOnlyPressSaveChangeButton")}</Alert>
          <Stack direction="row" justifyContent="space-between">
            <Button variant="outlined" onClick={closeModal}>
              {t("common:cancel")}
            </Button>
            <Button
              variant="outlined"
              color="success"
              disabled={!isNameFieldValid || selectedResources.length === 0}
              onClick={mode === "create" ? handleAddView : handleEditView}
            >
              {mode === "create" ? t("button:addViewToGroup") : t("button:confirmChanges")}
            </Button>
          </Stack>
        </Stack>
      }
    >
      {availableResources && availableResources.length > 0 ? (
        <Stack spacing={2}>
          <FormInputText
            validationProps={{
              formId: formId,
              fieldId: "name",
              required: true,
              initialState: mode === "update",
              initialValue: name,
            }}
            label={t("common:name")}
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={mode === "update"}
          />
          <Autocomplete
            popupIcon={<ExpandMoreRoundedIcon />}
            value={selectedResources}
            options={availableResources}
            getOptionLabel={(option) => option.name}
            multiple
            onChange={(_, value) => setSelectedResources(value)}
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                placeholder={t("field:placeholder.addResources")}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Stack>
      ) : (
        <Alert severity="info">
          {t("alert:info.noResourcesAvailable")}
          {", "}
          <Link variant="body1" color="text.primary" underline="hover" onClick={handleNavigateToCreateResources}>
            {t("common:clickHere")}
          </Link>
        </Alert>
      )}
    </Modal>
  );
});

export default ModalCreateOrUpdateResourceView;
